import { HttpErrorResponse } from "@angular/common/http";
import { ErrorHandler, Injectable, NgZone, inject } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { Store } from "@ngxs/store";
import * as Sentry from "@sentry/browser";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { StoredAuthState } from "../state/stored-auth.state";
import { NetworkErrorService } from "./network-error.service";
// NetworkErrorService will be imported in a constructor to avoid circular dependency

export enum ErrorSeverity {
	CRITICAL = "critical", // Application cannot function
	MAJOR = "major", // Feature broken but app can continue
	MINOR = "minor", // Non-critical issue
	INFO = "info", // Informational only
}

export interface ErrorContext {
	source?: string; // Component or service where error originated
	action?: string; // Action being performed when error occurred
	data?: any; // Any relevant data for debugging
	handled?: boolean; // Whether this error has been handled
}

export interface AppError {
	message: string;
	originalError: any;
	severity: ErrorSeverity;
	timestamp: Date;
	context?: ErrorContext;
	userId?: string;
	statusCode?: number;
}

@Injectable({
	providedIn: "root",
})
export class ErrorHandlerService implements ErrorHandler {
	private readonly store = inject(Store);
	private readonly snackBar = inject(MatSnackBar);
	private readonly router = inject(Router);
	private readonly ngZone = inject(NgZone);

	// Will be set in the constructor to avoid circular dependency
	private networkErrorService = inject(NetworkErrorService);

	private errorsSubject = new BehaviorSubject<AppError[]>([]);

	// Observable that components can subscribe to for error notifications
	errors$: Observable<AppError[]> = this.errorsSubject.asObservable();

	constructor() {}

	/**
	 * Main error handler implementation
	 */
	handleError(error: any, context?: ErrorContext): void {
		// Format the error
		const appError = this.formatError(error, context);

		// Add to the errors stream
		const currentErrors = this.errorsSubject.getValue();
		this.errorsSubject.next([...currentErrors, appError]);

		// Log to console in development
		this.logError(appError);

		// Send to monitoring service in production
		this.reportToMonitoring(appError);

		// Show user feedback if appropriate
		if (!context?.handled) {
			this.showUserFeedback(appError);
		}
	}

	/**
	 * Handle HTTP errors specifically
	 */
	handleHttpError(error: HttpErrorResponse, context?: ErrorContext) {
		// Determine severity based on status code
		let severity = ErrorSeverity.MINOR;

		if (error.status === 0) {
			severity = ErrorSeverity.MAJOR; // Network error
		} else if (error.status === 401 || error.status === 403) {
			severity = ErrorSeverity.MAJOR; // Authentication errors
		} else if (error.status >= 500) {
			severity = ErrorSeverity.MAJOR; // Server errors
		}

		const appError: AppError = {
			message: this.getHttpErrorMessage(error),
			originalError: error,
			severity,
			timestamp: new Date(),
			context,
			statusCode: error.status,
		};

		// Handle network errors with the network error service
		if (error.status === 0 && this.networkErrorService) {
			// Mark as handled so we don't show the snackbar
			this.handleError(appError, { ...context, handled: true });
			// Show network error modal
			this.networkErrorService.handleNetworkError(appError);
		} else {
			// Normal error handling for other errors
			this.handleError(appError, { ...context, handled: true });
		}

		// Special handling for auth errors
		if (error.status === 401) {
			this.ngZone.run(() => {
				// Redirect to login for auth errors
				this.router.navigate(["/auth"]);
			});
		}

		// Return a throwable for operators like catchError
		return throwError(() => appError);
	}

	/**
	 * Clear all recorded errors
	 */
	clearErrors(): void {
		this.errorsSubject.next([]);
	}

	/**
	 * Format raw error into AppError structure
	 */
	private formatError(error: any, context?: ErrorContext): AppError {
		// Handle HttpErrorResponse
		if (error instanceof HttpErrorResponse) {
			return {
				message: this.getHttpErrorMessage(error),
				originalError: error,
				severity: this.getSeverityFromHttpError(error),
				timestamp: new Date(),
				context,
				statusCode: error?.status,
			};
		}

		// Handle AppError that's passed through
		if (this.isAppError(error)) {
			return error;
		}

		// Get user information for reporting
		const user = this.store.selectSnapshot(StoredAuthState.user);
		const userId = user?.sub; // Using sub (subject identifier) instead of id which doesn't exist

		// Default error handling
		return {
			message: this.getErrorMessage(error),
			originalError: error,
			severity: ErrorSeverity.MINOR,
			timestamp: new Date(),
			context,
			userId,
		};
	}

	/**
	 * Check if an error is already an AppError
	 */
	private isAppError(error: any): error is AppError {
		return (
			error &&
			typeof error === "object" &&
			"message" in error &&
			"severity" in error &&
			"timestamp" in error
		);
	}

	/**
	 * Extract human-readable message from error
	 */
	private getErrorMessage(error: any): string {
		if (!error) {
			return "Unknown error occurred";
		}

		// Handle string errors
		if (typeof error === "string") {
			return error;
		}

		// Handle errors with message property
		if (error.message) {
			return error.message;
		}

		// Handle errors with error property (nested)
		if (error.error) {
			if (typeof error.error === "string") {
				return error.error;
			}
			if (error.error.message) {
				return error.error.message;
			}
		}

		// Default case
		return "An unexpected error occurred";
	}

	/**
	 * Extract human-readable message from HTTP error
	 */
	private getHttpErrorMessage(error: HttpErrorResponse): string {
		if (error.error instanceof ErrorEvent) {
			// Client-side error
			return `Client Error: ${error.error.message}`;
		}

		// Check for structured error response
		if (error.error && typeof error.error === "object") {
			if (error.error.message) {
				return error.error.message;
			}

			if (error.error.error) {
				return error.error.error;
			}
		}

		// Handle network errors
		if (error.status === 0) {
			return "Network error. Please check your internet connection";
		}

		// Handle auth errors
		if (error.status === 401) {
			return "Authentication error. Please log in again";
		}

		if (error.status === 403) {
			return "You do not have permission to perform this action";
		}

		// Handle server errors
		if (error.status >= 500) {
			return "Server error. Please try again later";
		}

		// Default with status code
		return `Error ${error.status}: ${error.statusText || "Unknown error"}`;
	}

	/**
	 * Determine error severity from HTTP status
	 */
	private getSeverityFromHttpError(error: HttpErrorResponse): ErrorSeverity {
		if (error.status === 0) {
			return ErrorSeverity.MAJOR; // Network error
		}

		if (error.status === 401 || error.status === 403) {
			return ErrorSeverity.MAJOR; // Auth errors
		}

		if (error.status >= 500) {
			return ErrorSeverity.MAJOR; // Server errors
		}

		return ErrorSeverity.MINOR; // Other errors (400s)
	}

	/**
	 * Log error to console in development
	 */
	private logError(error: AppError): void {
		// Only log in development
		if (console && console.error) {
			console.error("%c[APP ERROR]", "color: red; font-weight: bold", error);
		}
	}

	/**
	 * Report to monitoring service (Sentry)
	 */
	private reportToMonitoring(error: AppError): void {
		try {
			// Don't report minor errors
			if (
				error.severity === ErrorSeverity.INFO ||
				error.severity === ErrorSeverity.MINOR
			) {
				return;
			}

			// Get user context if available
			const user = this.store.selectSnapshot(StoredAuthState.user);
			const userContext = user
				? { username: user.email || user.phone_number || "unknown" }
				: undefined;

			Sentry.captureException(error.originalError || error, {
				level: this.getSentryLevel(error.severity),
				tags: {
					source: error.context?.source || "unknown",
					action: error.context?.action || "unknown",
				},
				extra: {
					context: error.context,
					timestamp: error.timestamp,
				},
				user: userContext,
			});
		} catch (e) {
			// Fail silently if Sentry reporting fails
			console.error("Error reporting to monitoring service", e);
		}
	}

	/**
	 * Map our severity levels to Sentry levels
	 */
	private getSentryLevel(severity: ErrorSeverity): Sentry.SeverityLevel {
		switch (severity) {
			case ErrorSeverity.CRITICAL:
				return "fatal";
			case ErrorSeverity.MAJOR:
				return "error";
			case ErrorSeverity.MINOR:
				return "warning";
			case ErrorSeverity.INFO:
				return "info";
			default:
				return "error";
		}
	}

	/**
	 * Show user feedback for errors
	 */
	private showUserFeedback(error: AppError): void {
		// Don't show for info-level errors
		if (error.severity === ErrorSeverity.INFO) {
			return;
		}

		// Run in NgZone to ensure UI updates
		this.ngZone.run(() => {
			// Customize based on severity
			let duration = 5000; // Default 5 seconds
			let panelClass = "error-snackbar";

			if (error.severity === ErrorSeverity.CRITICAL) {
				duration = 10000; // Show longer for critical errors
				panelClass = "critical-error-snackbar";
			} else if (error.severity === ErrorSeverity.MINOR) {
				panelClass = "warning-snackbar";
			}

			this.snackBar.open(error.message, "Dismiss", {
				duration,
				panelClass,
				horizontalPosition: "center",
				verticalPosition: "bottom",
			});
		});
	}
}
